import React, {
  FC,
  HTMLAttributes,
  ReactNode,
  useCallback,
  useState,
} from 'react';
import { usePopper } from 'react-popper';
import styled from 'styled-components';
import {
  Menu,
  MenuItem,
  propStyling,
  PropsWithPropStyling,
  tombac,
} from 'tombac';
import { UserIcon } from 'tombac-icons';
import useOutsideClick from '../../useOutsideClick';
import { useMeQuery } from '../authApi/authApiHooks';

export type LegolandNavbarMeProps = PropsWithPropStyling<
  HTMLAttributes<HTMLElement> & {
    breakpoint?: number;
    email?: string;
    menuItems?: ReactNode;
  }
>;

const Container = styled.div<Pick<LegolandNavbarMeProps, 'menuItems'>>`
  all: initial;
  align-items: center;
  cursor: ${({ menuItems }) => (menuItems ? 'pointer' : undefined)};
  display: flex;
  padding: ${tombac.space(1, 3)};
  ${propStyling}
`;

const DropIcon = styled.span.attrs(() => ({ children: '▾' }))`
  ${tombac.text()};
`;

const Email = styled.span`
  display: block;

  ${tombac.text({
    color: tombac.color('neutral', 600),
    fontSize: 12,
    fontWeight: 500,
  })}
`;

const LoggedIn = styled.span<
  Required<Pick<LegolandNavbarMeProps, 'breakpoint'>>
>`
  margin-right: ${tombac.space(1)};

  @media (max-width: ${({ breakpoint }) => tombac.unit(breakpoint)}) {
    display: none;
  }
`;

const LoggedInLabel = styled.span`
  display: block;
  ${tombac.altText({ fontSize: 12, fontWeight: 'bold' })}
`;

const LoggedInMenuItem = styled(MenuItem)<
  Required<Pick<LegolandNavbarMeProps, 'breakpoint'>>
>`
  border-bottom: ${tombac.unit(1)} solid ${tombac.color('neutral', 300)};
  display: none;
  margin-bottom: ${tombac.space(1)};
  min-width: ${tombac.space(18)};
  padding: ${tombac.space(1, 2, 2)};

  @media (max-width: ${({ breakpoint }) => tombac.unit(breakpoint)}) {
    display: block;
  }
`;

const UserImage = styled(UserIcon)<
  Required<Pick<LegolandNavbarMeProps, 'breakpoint'>>
>`
  background-color: ${tombac.color('neutral', 400)};
  border-radius: 50%;
  color: ${tombac.color('neutral', 600)};
  margin-right: ${tombac.space(1.5)};
  padding: ${tombac.unit(2)};

  @media (max-width: ${({ breakpoint }) => tombac.unit(breakpoint)}) {
    margin-right: ${tombac.space(1)};
  }
`;

export const LegolandNavbarMe: FC<LegolandNavbarMeProps> = ({
  breakpoint = 1024,
  menuItems,
  ...rest
}) => {
  const meQuery = useMeQuery();

  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLUListElement | null>(
    null,
  );
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-end',
  });

  const [isOpen, setIsOpen] = useState(false);

  const close = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  useOutsideClick(referenceElement, close);

  const loggedInLabel = <LoggedInLabel>Logged in as</LoggedInLabel>;

  if (!meQuery.isSuccess || !meQuery.data?.me) {
    return null;
  }

  const { me } = meQuery.data;

  return (
    <Container
      className="LegolandNavbarMe"
      menuItems={menuItems}
      onClick={() => setIsOpen(!isOpen)}
      ref={setReferenceElement}
      {...rest}
    >
      <UserImage breakpoint={breakpoint} />
      <LoggedIn breakpoint={breakpoint}>
        {loggedInLabel}{' '}
        <Email className="LegolandNavbarMe__email">{me.email}</Email>
      </LoggedIn>{' '}
      {menuItems && <DropIcon />}
      {menuItems && isOpen && (
        <Menu
          $minWidth="15sp"
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
        >
          <LoggedInMenuItem breakpoint={breakpoint}>
            {loggedInLabel} <Email>{me.email}</Email>
          </LoggedInMenuItem>
          {menuItems}
        </Menu>
      )}
    </Container>
  );
};
